import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import React, { Fragment, PropsWithChildren, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "components/typography";
import {
  Header,
  HoverVisibleCell,
  RowPadding,
  RowStatus,
  SortAscIcon,
  SortDescIcon,
  SortIconPlaceholder,
  StyledTable,
  StyledTd,
  StyledTh,
  StyledTr,
  StyledTrProps,
} from "./styles";

export const FALLBACK_VALUE = "--";

export type TRowMetadata = {
  rowMetadata?: {
    status?: RowStatus;
  };
};

export type RowData = {
  id: string;
};

type TableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  onRowClick?: (row: T) => void;
  onRowHover?: (rowId: number) => void;
  fixed?: boolean;
  styles?: {
    rowPadding?: RowPadding;
    rowStatus?: RowStatus;
    noMargin?: boolean;
    borderShadow?: boolean;
    isDropdown?: boolean;
  };
  columnVisibility?: Partial<Record<keyof T, boolean>>;
  rowAction?: (row: T) => React.ReactNode;
  rowContext?: (props: PropsWithChildren) => ReactNode;
  rowComponent?: (props: PropsWithChildren<StyledTrProps>) => ReactNode;
};

type TWithMetadata<T> = T & TRowMetadata;

export const Table = <T extends RowData>({
  data,
  columns,
  onRowClick,
  onRowHover,
  fixed = false,
  styles,
  columnVisibility,
  rowAction,
  rowContext: RowContext = Fragment,
  rowComponent: RowComponent = StyledTr,
}: TableProps<T>) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility: columnVisibility as Record<string, boolean>,
    },
  });

  return (
    <StyledTable
      $noMargin={!!styles?.noMargin}
      $borderShadow={!!styles?.borderShadow}
      $isDropdown={!!styles?.isDropdown}
      $fixed={fixed}
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <StyledTh
                  key={header.id}
                  id={`header-${header.id}`}
                  scope="col"
                  onClick={header.column.getToggleSortingHandler()}
                  $rowPadding={styles?.rowPadding}
                  style={{ width: fixed ? header.getSize() : undefined }}
                >
                  <Header data-title={t("SORT")}>
                    <Typography variant="body-x-small" insensitive>
                      {t(header.column.columnDef.header as string)}
                    </Typography>
                    {header.column.getIsSorted() === "asc" ? (
                      <SortAscIcon />
                    ) : header.column.getIsSorted() === "desc" ? (
                      <SortDescIcon />
                    ) : (
                      // without this placeholder, everything jumps when we conditionally render
                      // the sort icon
                      <SortIconPlaceholder />
                    )}
                  </Header>
                </StyledTh>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        <RowContext>
          {table.getRowModel().rows.map((row) => {
            return (
              <RowComponent
                key={row.id}
                $rowId={row.original.id}
                $isClickable={!!onRowClick}
                $rowStatus={
                  (row.original as TWithMetadata<T>).rowMetadata?.status
                }
                onClick={() => onRowClick?.(data[row.index])}
                onMouseEnter={() => onRowHover?.(row.index)}
              >
                {row.getVisibleCells().map((cell) => {
                  const value = cell.getValue();
                  const isString =
                    typeof value === "string" || typeof value === "number";
                  return (
                    <StyledTd
                      key={cell.id}
                      headers={`header-${cell.column.id}`}
                      data-testid={`header-${cell.column.id}`}
                      $rowPadding={styles?.rowPadding}
                    >
                      {isString ? (
                        <Typography
                          variant="body-small"
                          insensitive={
                            !(
                              cell.column.columnDef.meta as {
                                sensitive: boolean;
                              }
                            )?.sensitive
                          }
                        >
                          {value}
                        </Typography>
                      ) : (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )
                      )}
                    </StyledTd>
                  );
                })}
                {rowAction ? (
                  <HoverVisibleCell onClick={(e) => e.stopPropagation()}>
                    {rowAction(data[row.index])}
                  </HoverVisibleCell>
                ) : null}
              </RowComponent>
            );
          })}
        </RowContext>
      </tbody>
    </StyledTable>
  );
};
